/**
 * The field — what every control on every record form is wrapped in.
 *
 * `docs/record-flyouts.md` §3, drawn from
 * `24-record-flyouts/html/flyout-field-kit.html`. One label, one control, one
 * line of help, and — when it has one — one error in place of the help. The
 * numbers here all come from that file: 5px between the parts, an 11.5px
 * sentence-case muted label, 11px faint help, and a **5px green dot** where
 * forms used to print a red asterisk.
 *
 * The dot rather than the asterisk is not decoration. An asterisk is read out
 * as part of the field's name ("First name star"), it means nothing to
 * somebody who has not been taught it, and it puts a danger colour on a field
 * that is not in error. A filled dot in the accent says "this one is needed"
 * and stays quiet until the field actually fails.
 */
import { useId, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { IconWarn } from '../../frame/icons.js'

export interface FieldProps {
  label: string
  /** The green dot after the label. */
  required?: boolean
  /** "optional", at the label's right — for a field somebody might expect. */
  optional?: boolean
  /** One clause, no period. Replaced by `error` while there is one. */
  hint?: ReactNode
  error?: string
  /**
   * The control is a *group* rather than one box — a segmented control, a set
   * of chips, a colour picker, a drop zone.
   *
   * `<label for>` may only point at a labelable element (an input, a button,
   * a textarea), so pointing it at a `<div role="radiogroup">` is a label
   * naming nothing: the browser will not focus the control when you click the
   * word, and a screen reader reads the label and then falls silent. A group
   * says so here, gets `labelId` instead of `id`, and wires itself up with
   * `aria-labelledby`.
   */
  group?: boolean
  /** The control, given the ids the label and help are wired to. */
  children: (ids: {
    id: string
    /** The label element's own id — for a `group`'s `aria-labelledby`. */
    labelId: string
    describedBy?: string
    invalid: boolean
  }) => ReactNode
  className?: string
}

export function Field({
  label, required, optional, hint, error, group, children, className,
}: FieldProps) {
  const id = useId()
  const helpId = `${id}-help`
  const labelId = `${id}-label`
  const shown = error ?? hint

  return (
    <div className={cn('flex min-w-0 flex-col gap-[5px]', className)}>
      <span className="flex items-center text-fieldlabel text-text-muted">
        {group
          ? <span id={labelId}>{label}</span>
          : <label id={labelId} htmlFor={id}>{label}</label>}
        {required && (
          <span
            aria-hidden
            className="ml-[5px] inline-block h-[5px] w-[5px] shrink-0 rounded-full bg-accent"
          />
        )}
        {optional && <span className="ml-auto pl-2 text-[11px] text-text-faint">optional</span>}
      </span>

      {children({
        id, labelId, describedBy: shown ? helpId : undefined, invalid: Boolean(error),
      })}

      {error
        ? (
          <span
            id={helpId}
            role="alert"
            className="flex items-center gap-[5px] text-fieldlabel font-medium text-danger"
          >
            <span className="shrink-0"><IconWarn size={12} /></span>
            {error}
          </span>
        )
        : hint
          ? (
            <span id={helpId} className="text-[11px] leading-[1.4] text-text-faint">
              {hint}
            </span>
          )
          : null}
    </div>
  )
}

/**
 * The shared control box — 36px, radius 9, one border, one focus ring.
 *
 * Every part of the kit is this string plus its own contents, which is what
 * makes a form look like one form. The five states are the five the field kit
 * draws: ordinary, focused, error, disabled (the page's field grey), and
 * read-only (`surface-alt`, because it is showing you something rather than
 * refusing you).
 */
export function controlClass({
  invalid, disabled, readOnly, size = 'default', width = 'w-full', className,
}: {
  invalid?: boolean
  disabled?: boolean
  readOnly?: boolean
  /**
   * `big` is the 44px headline number — a quantity, a measure, a trip count.
   * `touch` is the 44px field of a full-screen phone form at 15px
   * (`new-request.md` Boards 14–16), where a 36px box is under a thumb.
   */
  size?: 'default' | 'big' | 'touch'
  /**
   * How wide, as **one** class.
   *
   * `w-full` by default, which is what a field in a grid does. A control that
   * sits in a sentence rather than a grid passes `w-auto shrink-0`; one with
   * a fixed size passes `w-[68px]`.
   *
   * It is a parameter rather than something a caller appends because `cn` is
   * a join, not a merge: `w-full w-[68px]` leaves two live rules and CSS
   * source order picks the winner. It picked `w-full` — which is how the
   * "When:" sentence on a package visit ended up five lines tall with two
   * 731px number boxes in it, *after* the widths had supposedly been set.
   */
  width?: string
  className?: string
} = {}): string {
  return cn(
    'flex items-center gap-1.5 rounded-control border px-[11px]',
    width,
    /*
     * One height class, chosen here rather than two appended and left to CSS
     * source order to settle. `cn` is a join, not a merge: "h-9 h-11" is two
     * live rules, and which one wins is whichever Tailwind happened to emit
     * last — the 44px field measured 36 until this became a choice.
     */
    size === 'big' ? 'h-11 text-[17px]'
      : size === 'touch' ? 'h-11 text-[15px]' : 'h-9 text-[13.5px]',
    'font-semibold text-text tnum',
    'placeholder:font-medium placeholder:text-text-faint',
    'focus:outline-none focus-visible:outline-none',
    invalid
      ? 'border-danger focus-within:ring-2 focus-within:ring-danger-ring focus:ring-2 focus:ring-danger-ring'
      : 'border-line focus-within:border-accent-line focus-within:ring-2'
        + ' focus-within:ring-accent-ring focus:border-accent-line focus:ring-2'
        + ' focus:ring-accent-ring',
    disabled && 'cursor-not-allowed bg-field text-text-faint',
    readOnly && !disabled && 'bg-surface-alt',
    !disabled && !readOnly && 'bg-surface',
    className,
  )
}

/**
 * A heading over a run of fields — 14/700 with a 12px muted sub beside it.
 *
 * On the same baseline, not above: "What it costs, and how far one goes ·
 * buying unit, allocation unit, and the conversion between them" is one
 * thought, and stacking the second half turns a heading into a paragraph.
 * No rule under it and no collapse; a long form scrolls.
 */
export function FormSection({ title, sub }: { title: string; sub?: ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 pb-0.5 pt-2 first:pt-0">
      <b className="shrink-0 text-[14px] font-bold text-text">{title}</b>
      {sub && <span className="min-w-0 text-[12px] leading-[1.35] text-text-muted">{sub}</span>}
    </div>
  )
}

/**
 * Two fields that are one sentence, with the word between them.
 *
 * "Bought by *Yard* **at** Cost per yd *$24.00*" is a single fact about a
 * material, and splitting it into two fields in two grid cells loses the
 * "at". The joiner sits on the control's baseline — `items-end`, so it lines
 * up with the boxes rather than with the labels above them.
 */
export function PairField({ left, joiner, right }: {
  left: ReactNode
  joiner: string
  right: ReactNode
}) {
  return (
    /*
     * On a phone the joiner goes and the two stack: "at" and "=" become the
     * order of the fields (Board 18, cells 2–3), because two 170px boxes side
     * by side at 390 is a pair nobody can read the labels of.
     */
    <div className="grid grid-cols-1 items-end gap-x-1.5 gap-y-3 sm:grid-cols-[1fr_34px_1fr] sm:gap-y-0">
      {left}
      <span aria-hidden className="hidden h-9 items-center justify-center text-[12.5px] text-text-faint sm:flex">
        {joiner}
      </span>
      {right}
    </div>
  )
}

export interface ReadoutItem {
  label: string
  /** Already formatted — "$24.00", "26%", "—". */
  value: string
  tone?: 'default' | 'accent' | 'danger'
}

/**
 * What the form worked out — a strip, not three more fields.
 *
 * Cost, price and margin are consequences of what was typed above, so they
 * are read-only by design and drawn as a result rather than an input. The
 * gutters are padding plus a hairline so the rule sits midway between two
 * numbers; the last one has none.
 */
export function Readout({ items }: { items: ReadoutItem[] }) {
  return (
    <div className="flex items-center rounded-[10px] border border-line-faint bg-surface-alt px-3.5 py-2.5">
      {items.map((item) => (
        <span
          key={item.label}
          className={cn('flex min-w-0 flex-col gap-[3px] border-r border-line-faint',
            'mr-[18px] pr-[18px] last:mr-0 last:border-r-0 last:pr-0')}
        >
          <span className="text-[11px] text-text-muted">{item.label}</span>
          <span className={cn('tnum text-[17px] font-bold leading-none',
            item.tone === 'accent' && 'text-accent',
            item.tone === 'danger' && 'text-danger',
            (!item.tone || item.tone === 'default') && 'text-text')}
          >
            {item.value}
          </span>
        </span>
      ))}
    </div>
  )
}

/**
 * The two-column grid a form's fields sit on — 12px rows, 14px columns.
 *
 * A field that is one thought on its own takes both columns (`FieldRow`'s
 * `wide`), which is why this is a component and not a class somebody
 * remembers: the grid and the span belong together.
 */
export function FormGrid({ children, className }: { children: ReactNode; className?: string }) {
  return (
    /*
     * One column on a phone. Two 170px fields side by side at 390px is a form
     * nobody can read a label on, and a `Wide` row inside one made the card
     * wider than the screen — the grid did not shrink, the card did not
     * shrink, and the right-hand column was simply cut off.
     */
    <div className={cn('grid grid-cols-1 gap-x-3.5 gap-y-3 sm:grid-cols-2', className)}>
      {children}
    </div>
  )
}

/** A cell that takes the whole grid width. */
export function Wide({ children, className }: { children: ReactNode; className?: string }) {
  // `col-span-full`, not `col-span-2`: in the phone's one-column grid a span
  // of two invents a second column and puts the card back over the edge.
  return <div className={cn('col-span-full min-w-0', className)}>{children}</div>
}
